import { useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import QRCode from 'qrcode.react';
import copy from 'copy-to-clipboard';
import {
  createStyles,
  makeStyles,
  CircularProgress,
  Button,
  SvgIconProps,
  SvgIcon,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../common/stores/store';
import { ITheme } from '../../common/theme';
import RecordDetailsForm from './RecordDetailsForm';
import InfoBox from '../../components/InfoBox';
import {certificatePath, recordsPath, searchPath} from '../../common/constants/routes';
import { DeveloperMode, FileCopyRounded } from '@material-ui/icons';
import ErrorInfoBox from '../../components/ErrorInfoBox';
import { useTranslation } from 'react-i18next';
import ProofCheck from '../../components/ProofCheck';

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    verticalAlign: { ...theme.mixins.verticalAlign, height: '100%' },
    wrapper: {
      ...theme.mixins.wrapper,
      minHeight: '80%',
      marginLeft: 'auto',
      marginRight: 'auto',
      width: '100%',
      padding: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        width: '90%',
      },
    },
    actionWrapper: {
      alignSelf: 'flex-start',
      padding: theme.spacing(1),
    },
    actionButton: {
      marginRight: theme.spacing(2),
    },
  })
);

const Certificate = () => {
  return <SvgIcon width="49" height="49" viewBox="0 0 24 24">
      <path
        d="M5 5h2v3h10V5h2v5h2V5c0-1.1-.9-2-2-2h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h6v-2H5V5zm7-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"></path>
      <path d="M21 11.5 15.51 17l-3.01-3-1.5 1.5 4.51 4.5 6.99-7z"></path>
  </SvgIcon>
}

const GetProof = (props: SvgIconProps) => (
  <SvgIcon {...props} width="49" height="51" viewBox="0 0 49 51">
    <path
      d="M37.6481 40.2352C37.9138 39.9737 38.0858 39.6008 38.0858 39.1915V34.1706L37.6481 40.2352ZM37.6481 40.2352L28.2918 49.5915L37.6481 40.2352ZM47.3788 12.2474L47.3789 12.2483C47.3903 12.3424 47.8036 15.9209 46.7183 20.3685C45.344 26.0002 42.1807 30.445 37.5575 33.3216L37.0858 33.6151V34.1706V39.1915C37.0858 39.3187 37.0316 39.4389 36.9466 39.5226L36.9465 39.5226L36.941 39.5281L27.5847 48.8844L27.5846 48.8846C27.5043 48.9649 27.3774 49.0206 27.2561 49.0206H27.256H27.2559H1.46484C1.20814 49.0206 1 48.8125 1 48.5558V1.48547C1 1.22877 1.20814 1.02063 1.46484 1.02063H36.6211C36.8778 1.02063 37.0859 1.22877 37.0859 1.48547V6.75784V7.17361L37.3807 7.46682C38.0422 8.12481 39.5295 9.47725 41.4501 10.4021L41.4502 10.4021C43.705 11.4877 46.8669 11.8299 46.9645 11.8402C47.1814 11.8629 47.3532 12.0328 47.3788 12.2474ZM2.92979 1.95032H1.92979V2.95032V47.0909V48.0909H2.92979H25.7911H26.7911V47.0909V39.1916C26.7911 38.9349 26.9993 38.7268 27.256 38.7268H35.1563H36.1563V37.7268V34.1707V33.6152L35.6846 33.3217C31.0615 30.4452 27.8981 26.0003 26.5239 20.3687C25.4388 15.9211 25.8522 12.3426 25.8635 12.2478L25.8635 12.2476C25.8893 12.0317 26.0612 11.8626 26.2774 11.8404L26.2774 11.8404L26.2832 11.8398L26.2871 11.8393L26.2969 11.8382L26.3295 11.8346C26.3568 11.8314 26.395 11.8269 26.4433 11.8209C26.5398 11.8089 26.6764 11.7909 26.8452 11.7664C27.1824 11.7173 27.6508 11.6413 28.1871 11.5324C29.2443 11.3177 30.628 10.9627 31.7923 10.4022L31.7924 10.4022C33.7121 9.47764 35.199 8.12592 35.8616 7.46681L36.1563 7.1736V6.75784V2.95032V1.95032H35.1563H2.92979ZM33.7911 41.3635L35.4982 39.6564H33.084H28.7207H27.7207V40.6564V45.0197V47.4339L29.4278 45.7268L33.7911 41.3635ZM36.0899 32.4814L36.6212 32.8146L37.1525 32.4814C41.5904 29.6983 44.5019 25.5306 45.8151 20.1482C46.4962 17.3573 46.5642 14.9246 46.5282 13.5574L46.5065 12.7351L45.6955 12.5978C42.3054 12.0235 39.9124 11.0394 37.2926 8.6658L36.6212 8.05743L35.9497 8.66581C33.3247 11.0443 30.9305 12.0245 27.5491 12.5975L26.7372 12.735L26.7164 13.5582C26.531 20.9187 29.404 28.2887 36.0899 32.4814Z"
      stroke="#FFFFFF"
      strokeWidth="3"
    />
    <path
      d="M36.4515 22.8467L35.6902 22.0855L33.2061 19.6015L33.2061 19.6015C33.0247 19.4201 32.7303 19.42 32.5487 19.6015C32.3673 19.7829 32.3672 20.0772 32.5487 20.2588C32.5488 20.2588 32.5488 20.2588 32.5488 20.2588L36.148 23.8579L36.148 23.858C36.3389 24.0489 36.6521 24.0376 36.8287 23.8328C36.8287 23.8328 36.8287 23.8328 36.8287 23.8328L42.3249 17.4592L42.325 17.459C42.4923 17.265 42.471 16.9716 42.2764 16.8038L36.4515 22.8467ZM36.4515 22.8467L37.1546 22.0314M36.4515 22.8467L37.1546 22.0314M37.1546 22.0314L41.6208 16.8523M37.1546 22.0314L41.6208 16.8523M41.6208 16.8523C41.7885 16.6579 42.0821 16.6363 42.2764 16.8037L41.6208 16.8523Z"
      stroke="#FFFFFF"
      strokeWidth="3"
    />
    <path
      d="M11.7187 12.7394H12.7187V11.7394V8.80969V7.80969H11.7187H8.78906H7.78906V8.80969V11.7394V12.7394H8.78906H11.7187ZM7.32422 6.88H13.1836C13.4403 6.88 13.6484 7.08815 13.6484 7.34485V13.2042C13.6484 13.4609 13.4403 13.6691 13.1836 13.6691H7.32422C7.06752 13.6691 6.85938 13.4609 6.85938 13.2042V7.34485C6.85938 7.08815 7.06752 6.88 7.32422 6.88Z"
      stroke="#FFFFFF"
      strokeWidth="3"
    />
    <path
      d="M11.7187 24.4581H12.7187V23.4581V20.5284V19.5284H11.7187H8.78906H7.78906V20.5284V23.4581V24.4581H8.78906H11.7187ZM7.32422 18.5988H13.1836C13.4403 18.5988 13.6484 18.8069 13.6484 19.0636V24.923C13.6484 25.1797 13.4403 25.3878 13.1836 25.3878H7.32422C7.06752 25.3878 6.85938 25.1797 6.85938 24.923V19.0636C6.85938 18.8069 7.06752 18.5988 7.32422 18.5988Z"
      stroke="#FFFFFF"
      strokeWidth="3"
    />
    <path
      d="M11.7187 36.1769H12.7187V35.1769V32.2472V31.2472H11.7187H8.78906H7.78906V32.2472V35.1769V36.1769H8.78906H11.7187ZM7.32422 30.3175H13.1836C13.4403 30.3175 13.6484 30.5256 13.6484 30.7823V36.6417C13.6484 36.8984 13.4403 37.1066 13.1836 37.1066H7.32422C7.06752 37.1066 6.85938 36.8984 6.85938 36.6417V30.7823C6.85938 30.5256 7.06752 30.3175 7.32422 30.3175Z"
      stroke="#FFFFFF"
      strokeWidth="2"
    />
  </SvgIcon>
);

interface IParams {
  id: string;
}
const RecordDetails = (): JSX.Element => {
  const { t } = useTranslation(['records', 'common']);
  const location = useLocation<{ comingFromSearch?: boolean }>();
  const classes = useStyles();
  const { recordStore, navigationStore, dialogStore } = useStore();
  const proofInputRef = useRef<HTMLInputElement>(null);
  const { id } = useParams<IParams>();

  useEffect(() => {
    if (id && !location.state?.comingFromSearch) {
      recordStore.details(id);
    }
    return () => recordStore.setSelectedRecord(null);
  }, [recordStore, id, location.state?.comingFromSearch]);

  const openSearch = () => {
    navigationStore.push(searchPath);
  };

  const openRecordsOverview = () => {
    navigationStore.push(recordsPath);
  };

  const copyUrlToClipboard = () => {
    copy(window.location.href);
  };

  if (recordStore.error) {
    const { error } = recordStore;
    return <ErrorInfoBox title={error.title} subtitle={error.message} />;
  }

  const openQrDialog = () => {
    dialogStore.setDialogConfig({
      open: true,
      onClose: dialogStore.closeDialog,
      onCancel: dialogStore.closeDialog,
      onConfirm: dialogStore.closeDialog,
      title: '',
      component: UrlQrCode,
      text: t('developer.qr_code.description'),
      singleButtonAction: dialogStore.closeDialog,
      singleButtonText: t('common:button.close'),
    });
  };

  const openProofDialog = () => {
    const recordHash = recordStore.selectedRecord?.file?.hash;
    const hashMethod = recordStore.selectedRecord?.file?.method;
    if (recordHash && hashMethod) {
      dialogStore.setDialogConfig({
        open: true,
        onClose: dialogStore.closeDialog,
        onCancel: dialogStore.closeDialog,
        onConfirm: dialogStore.closeDialog,
        title: t('proof.title'),
        component: () => (
          <ProofCheck
            fileInputRef={proofInputRef}
            hashMethod={hashMethod}
            hashToCheck={recordHash}
          />
        ),
        text: '',
        singleButtonAction: dialogStore.closeDialog,
        singleButtonText: t('common:button.close'),
      });
    }
  };

  const navigateToCert = () => {
    navigationStore.push(`${certificatePath}?form=formId&id=${id}`);
  }

  const hasForm = false;

  return (
    <div className={classes.wrapper}>
      {recordStore.recordDetailsLoading ? (
        <div className={classes.verticalAlign}>
          <CircularProgress color={'primary'} />
        </div>
      ) : recordStore.selectedRecord ? (
        <>
          <div className={classes.actionWrapper}>
            <Button
              className={classes.actionButton}
              color="primary"
              variant="contained"
              onClick={copyUrlToClipboard}
            >
              <FileCopyRounded />
            </Button>
            <Button
              className={classes.actionButton}
              color="primary"
              variant="contained"
              onClick={openQrDialog}
            >
              <DeveloperMode />
            </Button>
            {recordStore.selectedRecord?.file?.hash && (
              <Button
                className={classes.actionButton}
                color="primary"
                variant="contained"
                onClick={openProofDialog}
              >
                <GetProof />
              </Button>
            )}
            {hasForm && (
              <Button
                className={classes.actionButton}
                color="primary"
                variant="contained"
                onClick={navigateToCert}
              >
                <Certificate />
              </Button>
            )}
          </div>
          <RecordDetailsForm />
        </>
      ) : (
        <div className={classes.verticalAlign}>
          <InfoBox
            title={t('common.no_record_found.title')}
            subtitle={t('common.no_record_found.subtitle')}
            leftButton={{
              text: t('common.no_record_found.new_search'),
              variant: 'contained',
              onClick: openSearch,
            }}
            rightButton={{
              text: t('common.no_record_found.all_records'),
              variant: 'outlined',
              onClick: openRecordsOverview,
            }}
          />
        </div>
      )}
    </div>
  );
};

const UrlQrCode = () => (
  <QRCode value={window.location.href} size={256} includeMargin={true} />
);

export default observer(RecordDetails);
